/**
 * VeraLok Developer Portal — API consumer surface (Session N).
 *
 * Dedicated portal shell parallel to Admin / Institution / Demo.
 * Content is the existing comprehensive `pages/Developer.jsx` (credentials,
 * webhooks, usage, rate limits, docs) — restored into a proper portal
 * layout with sidebar navigation, header, and role gating.
 *
 * Backend permissions (rbac.py):
 *   developer:read   → system_admin, institution_admin, compliance_officer
 *   developer:manage → system_admin, institution_admin
 * All API calls tenant-scope by design when the caller is `institution_admin`.
 */
import React from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DeveloperRoute } from "./PortalGuards";
import VeraLokLogo from "@/marketing/VeraLokLogo";
import { PortalHeaderUserMenu } from "./PortalHeaderUserMenu";
import "@/marketing/design-system.css";

import Developer from "@/pages/Developer";

const SECTIONS = [
  { key: "overview", label: "Overview", path: "" },
  { key: "credentials", label: "API Credentials", path: "credentials" },
  { key: "webhooks", label: "Webhooks", path: "webhooks" },
  { key: "usage", label: "Usage & Analytics", path: "usage" },
  { key: "rate-limits", label: "Rate Limits", path: "rate-limits" },
  { key: "docs", label: "Docs & SDKs", path: "docs" },
];

/**
 * Because `pages/Developer.jsx` owns its own tab state, we render the same
 * component on every sub-route and let it manage its own tab. The sidebar
 * links pass a query hint the component reads on mount so a bookmark to
 * `/developer/webhooks` opens on the Webhooks tab.
 */
function DeveloperContent({ initialTab }) {
  React.useEffect(() => {
    if (initialTab && typeof window !== "undefined") {
      window.__vlk_dev_initial_tab = initialTab;
    }
  }, [initialTab]);
  return <Developer />;
}

export default function DeveloperShell() {
  return (
    <DeveloperRoute>
      <div className="vlk-portal vlk-portal--developer min-h-screen flex" data-testid="developer-shell">
        <DeveloperSidebar />
        <main className="flex-1 min-w-0">
          <DeveloperHeaderStrip />
          <div className="p-6">
            <Routes>
              <Route path="" element={<DeveloperContent />} />
              <Route path="credentials" element={<DeveloperContent initialTab="credentials" />} />
              <Route path="webhooks" element={<DeveloperContent initialTab="webhooks" />} />
              <Route path="usage" element={<DeveloperContent initialTab="usage" />} />
              <Route path="rate-limits" element={<DeveloperContent initialTab="rate-limits" />} />
              <Route path="docs" element={<DeveloperContent initialTab="docs" />} />
              <Route path="*" element={<Navigate to="" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </DeveloperRoute>
  );
}

function DeveloperHeaderStrip() {
  const { user } = useAuth();
  return (
    <div className="vlk-portal-header vlk-portal-header--developer px-6 py-3 flex items-center justify-between text-xs"
         data-testid="developer-header-strip">
      <div className="text-white/60 flex items-center gap-3">
        <VeraLokLogo size={22} showWordmark={false} />
        <span className="font-semibold text-white/80">Developer Portal</span>
        <span className="text-white/30">·</span>
        <span className="text-white/50 hidden md:inline" data-testid="developer-header-descriptor">API Credentials · Webhooks · Rate Limits · Docs</span>
        <span className="text-white/30 hidden md:inline">·</span>
        <span className="vlk-portal-role-pill vlk-portal-role-pill--developer">API Integration</span>
      </div>
      <div className="text-white/70 flex items-center gap-3">
        <PortalHeaderUserMenu variant="developer" testIdPrefix="developer" />
      </div>
    </div>
  );
}

function DeveloperSidebar() {
  return (
    <aside className="vlk-portal-sidebar w-60 p-4 space-y-1 shrink-0" data-testid="developer-sidebar">
      <div className="mb-4 flex items-center gap-2">
        <VeraLokLogo size={24} />
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 text-violet-300/80" data-testid="developer-sidebar-section-label">
        Integration
      </div>
      {SECTIONS.map((s) => (
        <NavLink
          key={s.key}
          to={`/developer/${s.path}`}
          end={s.path === ""}
          className={({ isActive }) => `vlk-portal-nav-link ${isActive ? "active" : ""}`}
          data-testid={`developer-nav-${s.key}`}
        >
          {s.label}
        </NavLink>
      ))}
    </aside>
  );
}
